import { useState } from "react";
import { CalendarClock, CheckCircle2, LifeBuoy, MessageCircle, UserRound } from "lucide-react";
import { FEATURES } from "../../config/features";

const STATUS = {
  open: ["Ouverte", "bg-[#FFF4E5] text-[#b26a00]"],
  answered: ["Répondue", "bg-[#E9FBF0] text-[#128a3e]"],
  booked: ["Séance proposée", "bg-[#EAF2FF] text-[#2457a6]"],
  transferred: ["Transmise au tuteur", "bg-[#F1ECFF] text-[#5b3fb0]"],
  resolved: ["Traitée", "bg-gray-100 text-[#4a5f53]"],
};

const LINE = {
  ask: "a demandé de l’aide",
  reply: "a répondu",
  book: "propose une séance",
  booking_accept: "a accepté la séance",
  booking_change: "demande un autre moment",
  transfer: "a passé la main au tuteur",
  resolve: "a marqué la demande comme traitée",
};

const btn = "rounded-full px-3 py-1.5 text-[12px] font-semibold disabled:opacity-50";

export function whenLabel(at) {
  if (!at) return "";
  const d = new Date(at.length === 16 ? `${at}:00` : at);
  return d.toLocaleString("fr-CA", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });
}

const Thread = ({ item }) => (
  <ul className="mt-2 space-y-1" data-testid="help-thread">
    {item.thread.map((event, index) => (
      <li key={`${event.at}-${index}`} className="text-[12px] text-[#4a5f53]">
        <span className="font-semibold text-[#122118]">{event.role === "parent" ? "Parent" : item.studentName || "Élève"}</span>{" "}
        {LINE[event.kind] || event.kind}
        {event.kind === "book" && item.booking?.at ? ` : ${whenLabel(item.booking.at)}` : ""}
        {event.text ? ` — « ${event.text} »` : ""}
      </li>
    ))}
  </ul>
);

/** Parent actions: reply, book a time, hand over to a tutor, mark as handled. */
const ParentActions = ({ item, run, busy }) => {
  const [mode, setMode] = useState("");
  const [text, setText] = useState("");
  const [at, setAt] = useState("");
  const done = (fn) => run(fn).then((ok) => ok && (setMode(""), setText(""), setAt("")));
  return (
    <div className="mt-3">
      {mode === "reply" && (
        <div className="mb-2">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={500} placeholder="Votre réponse" className="w-full rounded-xl border border-gray-200 px-3 py-2 text-[14px] outline-none focus:border-[#25D366]" data-testid="help-reply-text" />
          <button type="button" disabled={busy || !text.trim()} onClick={() => done((api) => api.replyHelp(item.id, text.trim()))} className={`${btn} mt-1 bg-[#25D366] text-[#0a2914]`} data-testid="help-reply-send">Envoyer</button>
        </div>
      )}
      {mode === "book" && (
        <div className="mb-2">
          <input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} className="w-full rounded-xl border border-gray-200 px-3 py-2 text-[14px]" data-testid="help-book-at" />
          <input value={text} onChange={(e) => setText(e.target.value)} maxLength={200} placeholder="Note (facultatif)" className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-[14px]" data-testid="help-book-note" />
          <button type="button" disabled={busy || !at} onClick={() => done((api) => api.bookHelp(item.id, at, text.trim()))} className={`${btn} mt-1 bg-[#25D366] text-[#0a2914]`} data-testid="help-book-send">Proposer ce moment</button>
        </div>
      )}
      <div className="flex flex-wrap gap-1.5">
        <button type="button" onClick={() => setMode(mode === "reply" ? "" : "reply")} className={`${btn} border border-gray-200 text-[#2d5a3d]`} data-testid="help-action-reply"><MessageCircle className="mr-1 inline h-3.5 w-3.5" />Répondre</button>
        <button type="button" onClick={() => setMode(mode === "book" ? "" : "book")} className={`${btn} border border-gray-200 text-[#2d5a3d]`} data-testid="help-action-book"><CalendarClock className="mr-1 inline h-3.5 w-3.5" />Réserver une séance</button>
        {item.status !== "transferred" && (
          <button type="button" disabled={busy} onClick={() => run((api) => api.transferHelp(item.id))} className={`${btn} border border-gray-200 text-[#2d5a3d]`} data-testid="help-action-transfer"><UserRound className="mr-1 inline h-3.5 w-3.5" />Passer au tuteur</button>
        )}
        {FEATURES.aiHelp && (
          <button type="button" className={`${btn} border border-gray-200 text-[#2d5a3d]`} data-testid="help-action-ai">Aide de l’IA</button>
        )}
        <button type="button" disabled={busy} onClick={() => run((api) => api.resolveHelp(item.id))} className={`${btn} bg-[#122118] text-white`} data-testid="help-action-resolve"><CheckCircle2 className="mr-1 inline h-3.5 w-3.5" />Traité</button>
      </div>
    </div>
  );
};

/** Student answer to a proposed time: accept or ask for another moment. */
const StudentBooking = ({ item, run, busy }) => {
  const [change, setChange] = useState(false);
  const [text, setText] = useState("");
  if (item.booking?.status !== "proposed") return null;
  return (
    <div className="mt-3">
      {change && (
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={200} placeholder="Quel moment te convient mieux ?" className="mb-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-[14px]" data-testid="help-change-text" />
      )}
      <div className="flex gap-1.5">
        {!change && <button type="button" disabled={busy} onClick={() => run((api) => api.answerBooking(item.id, "accept", ""))} className={`${btn} bg-[#25D366] text-[#0a2914]`} data-testid="help-booking-accept">Accepter</button>}
        <button type="button" disabled={busy || (change && !text.trim())} onClick={() => (change ? run((api) => api.answerBooking(item.id, "change", text.trim())) : setChange(true))} className={`${btn} border border-gray-200 text-[#2d5a3d]`} data-testid="help-booking-change">
          {change ? "Envoyer" : "Demander un autre moment"}
        </button>
      </div>
    </div>
  );
};

export const HelpCard = ({ request, api, viewer, mobileType = false, onChange }) => {
  const [item, setItem] = useState(request);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [label, cls] =
    item.status === "booked" && item.booking?.status === "accepted"
      ? ["Séance acceptée", "bg-[#E9FBF0] text-[#128a3e]"]
      : item.status === "booked" && item.booking?.status === "change_requested"
      ? ["Autre moment demandé", "bg-[#FFF4E5] text-[#b26a00]"]
      : STATUS[item.status] || STATUS.open;
  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      const next = await fn(api);
      setItem(next);
      onChange?.(next, next.thread[next.thread.length - 1]?.kind);
      return true;
    } catch (problem) {
      setError(problem.message || "Action impossible.");
      return false;
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="w-[94%] sm:w-[88%] max-w-[23rem] self-start rounded-2xl rounded-tl-sm bg-white p-2.5 sm:p-3 shadow-sm" data-testid={`help-card-${item.id}`}>
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#FFF4E5] text-[#b26a00]"><LifeBuoy className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <p className={`truncate font-semibold text-[#122118] ${mobileType ? "text-[15px]" : "text-[13px]"}`}>{item.planTitle}</p>
          <p className="text-[11px] text-[#829C8D]">{viewer === "parent" ? `${item.studentName || "Votre enfant"} bloque` : "Ta demande d’aide"}</p>
        </div>
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${cls}`} data-testid="help-status">{label}</span>
      </div>
      {item.booking?.at && (
        <p className="mt-2 rounded-xl bg-[#EAF2FF] px-3 py-2 text-[12px] text-[#2457a6]" data-testid="help-booking">
          Séance : {whenLabel(item.booking.at)}
          {item.booking.status === "accepted" ? " · acceptée" : item.booking.status === "change_requested" ? " · autre moment demandé" : " · en attente"}
        </p>
      )}
      <Thread item={item} />
      {error && <p className="mt-2 text-xs text-[#9b3b3b]" data-testid="help-error">{error}</p>}
      {item.status !== "resolved" && viewer === "parent" && <ParentActions item={item} run={run} busy={busy} />}
      {item.status !== "resolved" && viewer !== "parent" && <StudentBooking item={item} run={run} busy={busy} />}
    </div>
  );
};

export default HelpCard;
